// The Closed tab's "Closed between" range and Outcome filter (spec C7.3, Renewals board; S16). Pure
// functions, so the rules are tested without a browser. The range lives in the address: the list
// loader sends it to the server, which adds the renewals closed in it to the list.
import type { ClosedOutcome, RenewalRow } from '../auth/api';
import { parseDate } from './fix-lines';
import { listDate, nyToday } from './renewal-lines';

export type ClosedPreset = 'this' | 'last' | 'l90' | 'custom';

/** The presets, as the spec and the board name them; Last 90 days is the default. */
export const closedPresets: { key: ClosedPreset; label: string }[] = [
  { key: 'this', label: 'This month' },
  { key: 'last', label: 'Last month' },
  { key: 'l90', label: 'Last 90 days' },
  { key: 'custom', label: 'Custom' },
];
export const defaultClosedPreset = 'l90' as const;

/** The longest range the server takes (maxClosedDays in apps/server/src/renewals.ts). */
export const maxClosedDays = 400;

/**
 * Every outcome the spec names, in one shared list: `ready` once the portal can tell it. Rewritten,
 * Lost and Not renewed come from the AE's Close it myself (S15 batch 6c), Policy inactive with its
 * branch.
 */
export const outcomeList: { key: string; label: string; ready: boolean }[] = [
  { key: 'renewed', label: 'Renewed', ready: true },
  { key: 'rewritten', label: 'Rewritten', ready: true },
  { key: 'lost', label: 'Lost', ready: true },
  { key: 'nonrenewed', label: 'Not renewed', ready: true },
  { key: 'inactive', label: 'Policy inactive', ready: true },
  { key: 'paid', label: 'Need payment then paid', ready: true },
  { key: 'cancelled', label: 'Cancelled', ready: true },
  { key: 'sheet', label: 'Closed from renewal sheet', ready: true },
];
export const outcomeOptions = outcomeList.filter((o) => o.ready);
export const outcomeLabel = (key: string) => outcomeList.find((o) => o.key === key)?.label ?? key;
export const isOutcome = (v: string): v is ClosedOutcome => outcomeOptions.some((o) => o.key === v);

const ymd = (d: Date) => d.toISOString().slice(0, 10);
const shift = (day: string, days: number) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return ymd(d);
};

/** The days of a preset, counted from today in New York. */
export function presetDays(preset: Exclude<ClosedPreset, 'custom'>, today: string) {
  const [y, m] = today.split('-').map(Number);
  if (preset === 'this') return { from: `${today.slice(0, 7)}-01`, to: today };
  if (preset === 'last') {
    const first = ymd(new Date(Date.UTC(y, m - 2, 1)));
    return { from: first, to: ymd(new Date(Date.UTC(y, m - 1, 0))) };
  }
  return { from: shift(today, -89), to: today };
}

/** Two typed custom days that make a range the server takes, or null. */
export function customDays(from: string | null, to: string | null) {
  if (!from || !to || !parseDate(from) || !parseDate(to)) return null;
  const f = parseDate(from)!;
  const t = parseDate(to)!;
  if (f > t || shift(f, maxClosedDays) < t) return null;
  return { from: f, to: t };
}

export type ClosedRange = { preset: ClosedPreset; from: string; to: string };

/**
 * The Closed between range from the address: `closed` (a preset) and, for Custom, `cfrom` and `cto`.
 * A Custom range without two good days loads the default days.
 */
export function closedRangeOf(params: URLSearchParams, today: string): ClosedRange {
  const raw = params.get('closed');
  const preset = closedPresets.some((p) => p.key === raw)
    ? (raw as ClosedPreset)
    : defaultClosedPreset;
  if (preset === 'custom') {
    const days = customDays(params.get('cfrom'), params.get('cto'));
    return { preset, ...(days ?? presetDays(defaultClosedPreset, today)) };
  }
  return { preset, ...presetDays(preset as Exclude<ClosedPreset, 'custom'>, today) };
}

/** The range as the address keeps it; the default is left out. */
export function closedRangeParams(r: { preset: ClosedPreset; from?: string; to?: string }) {
  return {
    closed: r.preset === defaultClosedPreset ? null : r.preset,
    cfrom: r.preset === 'custom' ? (r.from ?? null) : null,
    cto: r.preset === 'custom' ? (r.to ?? null) : null,
  };
}

/** Whether a list reload is needed: the range decides which closed renewals the server sends. */
export const sameClosedRange = (a: URLSearchParams, b: URLSearchParams) =>
  ['closed', 'cfrom', 'cto'].every((k) => a.get(k) === b.get(k));

/** The chip's label: the preset's name, or "Oct 1 to Oct 31" for Custom. */
export const closedRangeLabel = (r: ClosedRange) =>
  r.preset === 'custom'
    ? `${listDate(r.from)} to ${listDate(r.to)}`
    : closedPresets.find((p) => p.key === r.preset)!.label;

/** The New York day a renewal was closed, or null when it is not closed. */
export const closedDay = (r: RenewalRow) => (r.closed ? nyToday(new Date(r.closed.at)) : null);

/** Closed on one of the range's days. */
export function inClosedRange(r: RenewalRow, range: { from: string; to: string }): boolean {
  const day = closedDay(r);
  return day !== null && day >= range.from && day <= range.to;
}

/** "No renewals closed this month." for an empty range. */
export const noClosedIn = (r: ClosedRange) =>
  r.preset === 'custom'
    ? `No renewals closed ${listDate(r.from)} to ${listDate(r.to)}.`
    : `No renewals closed ${closedPresets.find((p) => p.key === r.preset)!.label.toLowerCase()}.`;

/** A day as the date inputs show it: "Oct 1, 2026" (kit Input: typed dates). */
export const typedDay = (ymd: string) => `${listDate(ymd)}, ${ymd.slice(0, 4)}`;

/** Why the typed Custom days are not a range yet, or null when they are (or nothing is typed). */
export function customError(from: string, to: string): string | null {
  if (!from.trim() || !to.trim()) return null;
  if (!parseDate(from) || !parseDate(to)) return 'Enter dates like Oct 30, 2026.';
  if (parseDate(from)! > parseDate(to)!) return 'From comes before To.';
  if (!customDays(from, to)) return `Pick at most ${maxClosedDays} days.`;
  return null;
}
